<template>
  <div class="player has-background-white-bis">
    <section class="hero">
      <div class="pb-1">
        <div class="container is-max-desktop has-text-centered">
          <h1 class="title is-4 is-size-5-mobile pt-5 px-5" :key="videoId + 'title'">
            {{ title }}
            <b-skeleton width="60%" size="is-medium" position="is-centered" :active="loading">
            </b-skeleton>
          </h1>
          <p class="is-siez-7" :key="videoId + 'channel'">
            <b-skeleton width="10%" size="is-small" position="is-centered" :active="loading">
            </b-skeleton>
            <span class="has-text-grey-light" v-if="channel.provider_meta">
              <a :href="'https://www.youtube.com/channel/' + channel.provider_meta.youtube_channel_id" class="has-text-grey-dark" rel="noreferrer noopener" target="_blank">
                {{ channel.title }}
                <b-skeleton width="20%" position="is-centered" :active="loading"></b-skeleton>
              </a>
            </span>
          </p>
        </div>

        <div class="is-flex is-justify-content-center is-align-content-center mt-5">
          <b-button
            class="mx-1 px-5 has-text-weight-semibold stick-button"
            :label="$t('general.share')"
            type="is-dark"
            @click="shareModal"
            outlined
          />
          <b-button
            class="mx-1 px-5 has-text-weight-semibold stick-button"
            :label="isFavorited ? $t('general.liked') : $t('general.like')"
            :type="isFavorited ? 'is-static' : 'is-black'"
            @click="addVideoFromFavorite(videoId)"
          />
        </div>

      </div>
    </section>

    <section class="section">
      <div class="columns is-centered is-mobile has-text-centered">
        <div class="column is-2-desktop" style="border-right: 1px solid #dedede;">
          <p class="has-text-weight-semibold has-text-grey-dark is-size-7">
            {{ $t('general.instruments') }}
          </p>
          <p class="has-text-weight-bold is-size-6 is-text-overflow">
            <b-skeleton height="35px" width="50%" size="is-small"
              position="is-centered" :active="loading">
            </b-skeleton>
            <router-link :to='"/instruments/" + tag.slug'
              v-for="tag in instrumentTags.slice(0, 1)"
              :key="tag.slug" class="has-text-black">
              {{ tag.trans }}
            </router-link>
          </p>
          <p class="is-size-7">
            <button class="button is-text is-small" v-if="instrumentTags.length > 1"
              @click="isInstrumentsActive = true">
              {{ $t('general.more') }}
            </button>
            <span v-else>-</span>
          </p>
        </div>
        <div class="column is-2-desktop">
          <p class="has-text-weight-semibold has-text-grey-dark is-size-7">
            {{ $t('general.genre') }}
          </p>
          <p class="has-text-weight-bold is-size-6">
            <b-skeleton height="35px" width="50%" size="is-small"
              position="is-centered" :active="loading">
            </b-skeleton>
            <router-link :to='"/genre/" + tag.slug'
              v-for="tag in genreTags.slice(0, 1)"
              :key="tag.slug" class="has-text-black">
              {{ tag.trans || tag.name }}
            </router-link>
          </p>
          <p class="is-size-7">
            <button class="button is-text is-small" v-if="genreTags.length > 1"
              @click="isGenreActive = true">
              {{ $t('general.more') }}
            </button>
            <span v-else>-</span>
          </p>
        </div>
      </div>
    </section>

    <section class="section px-5 pt-0" :key="videoId + 'profile'">
      <div class="container is-max-widescreen">
        <div class="has-text-centered">
          <small class="has-text-weight-semibold has-text-grey-dark">
            <b-skeleton width="10%" size="is-small" position="is-centered" :active="loading">
            </b-skeleton>
            {{ created_at ? (new Date(created_at)).toLocaleDateString() : '' }}
          </small>
        </div>
        <div class="columns">
          <div class="column is-three-fifths is-offset-one-fifth">
            <p class="pt-2 pb-3" style="white-space: pre-line; word-wrap:break-word;">
              <foldable :height="100" async>
                <b-skeleton :count="4" size="is-medium" :active="loading"></b-skeleton>
                {{ description }}
                <p align="center"
                  class="pt-3 is-size-7 has-text-weight-bold is-uppercase"
                  slot="view-more" slot-scope="{ toggle, collapsed }"
                  @click="toggle(), moreDescription()"
                >
                  <b-icon pack="fas" :icon="collapsed ? 'angle-down' : 'angle-up'"></b-icon>
                  <!-- {{ collapsed ? $t('performs.showmore') : $t('performs.showless') }} -->
                </p>
              </foldable>
            </p>
          </div>
        </div>
      </div>

      <div class="divider div-transparent my-5"></div>

      <div class="container is-max-widescreen">
        <div class="columns">
          <div class="column is-10 is-offset-1">
            <b-skeleton height="30px" :count="1" size="is-large" :active="loading"></b-skeleton>
            <vue-horizontal class="taghorizontal" :displacement="0.7" snap="none">
              <b-button size="is-small"
                class="mr-3"
                type="is-light"
                tag="router-link"
                :to="{ path: '/tags/' + tag.slug }"
                v-for="tag in tags"
                :key="tag.slug">
                {{ tag.trans || tag.name }}
              </b-button>
            </vue-horizontal>
          </div>
        </div>
      </div>
    </section>

    <section class="section has-background-light pt-5" :key="videoId + 'related'">
      <div class="container has-text-centered pb-3">
        <nav class="level">
          <div class="level-left">
            <h1 class="title is-6 has-text-weight-bold">{{ $t('performs.related') }}</h1>
          </div>
          <div class="level-right">
            <p class="has-text-weight-bold is-size-7 px-2">
              {{ $t('performs.autoplaynext') }}
            </p>

            <b-switch :value="true"
                      :rounded="false"
                      class="m-0"
                      size="is-small"
                      type="is-dark"
                      v-model="isPlayNextVideo"
                      @input="changeIsPlayNext"
                      aria-label="Autoplay next"
            >
              <!-- {{ isPlayNextVideo ? 'Yes' : 'No'}} -->
            </b-switch>
          </div>
        </nav>
      </div>

      <div class="container" v-if="loading">
        <div class="columns is-multiline is-mobile">
          <div class="column is-one-quarter-desktop is-half-tablet is-half-mobile"
            v-for="i in media" :key="i">
            <div class="media-content">
              <figure class="image is-16by9 skeleton">
                <div class="skeleton-block"></div>
              </figure>
              <b-skeleton></b-skeleton>
              <span class="icon is-small">
                  <b-skeleton></b-skeleton>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="container">
        <div class="columns is-multiline is-mobile">
          <div class="column is-one-quarter-desktop is-half-tablet is-half-mobile"
            v-for="item in relatedList" :key="item.id">
            <router-link :to='"/video/" + item.id' :title="item.title">
              <video-item
                :channel-title="item.channel.title"
                :youtube-id="item.provider_video_id"
                :title="item.title" >
              </video-item>
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <b-modal v-model="isInstrumentsActive" :width="300" scroll="keep">
      <div class="card">
        <div class="card-content">
          <div class="media">
            <div class="media-content">
                <p class="title is-4">{{ $t('general.instruments') }}</p>
            </div>
          </div>

          <div class="content">
            <div class="buttons mb-1">
              <b-button size="is-small"
                        type="is-dark"
                        tag="router-link"
                        :to="{ path: '/instruments/' + tag.slug }"
                        v-for="tag in instrumentTags"
                        :key="tag.slug">
                {{ tag.trans || tag.name }}
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
    <b-modal v-model="isGenreActive" :width="300" scroll="keep">
      <div class="card">
        <div class="card-content">
          <div class="media">
            <div class="media-content">
                <p class="title is-4">{{ $t('general.genre') }}</p>
            </div>
          </div>

          <div class="content">
            <div class="buttons mb-1">
              <b-button size="is-small"
                        type="is-dark"
                        tag="router-link"
                        :to="{ path: '/genre/' + tag.slug }"
                        v-for="tag in genreTags"
                        :key="tag.slug">
                {{ tag.trans || tag.name }}
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </b-modal>

  </div>
</template>

<script>
import VueHorizontal from 'vue-horizontal';
import ShareModal from 'components/ShareModal.vue';
import {
  getVideoById, getRelatedVideos, addFavoriteVideo, getFavoriteStatus,
} from '@/api/api';
import { mapGetters, mapState } from 'vuex';
import store from '@/store';

export default {
  name: 'VideoPage',
  components: { VueHorizontal },

  metaInfo() {
    return {
      title: this.title,
      meta: [
        { name: 'description', vmid: 'description', content: this.description.slice(0, 50) },
        { property: 'og:title', vmid: 'og:title', content: this.title },
        { property: 'og:image', vmid: 'og:image', content: `https://i.ytimg.com/vi/${this.youtubeId}/mqdefault.jpg` },
        { property: 'og:description', vmid: 'og:description', content: this.description.slice(0, 50) },
      ],
    };
  },

  data() {
    return {
      isInstrumentsActive: false,
      isGenreActive: false,
      playerVars: {
        autoplay: 1,
        loop: 1,
        fs: 1,
        rel: 0,
        controls: 1,
        autohide: 1,
        iv_load_policy: 3,
        modestbranding: 1,
        playsinline: 1,
      },

      videoId: '',

      loading: true,
      isPlayNextVideo: false,
      media: 4,

      currentUrl: '',
      title: '',
      youtubeId: '',
      description: '',
      channel: {
        id: null,
        title: null,
        provider_meta: null,
      },
      created_at: null,
      instrumentTags: [],
      genreTags: [],
      tags: [],
      relatedList: [],

      isFavorited: false,
    };
  },
  computed: {
    ...mapGetters({
      player: 'player',
      user: 'user',
    }),

    ...mapState({
      user: 'user',
    }),

    isQueueHasOtherVideo() {
      return this.player.queue.length > 1;
    },
  },

  watch: {
    '$store.state.player.state': function () {
      const playerState = this.$store.state.player.state;
      let nextVideoId;

      if (this.isPlayNextVideo && (playerState === 0) && !this.isQueueHasOtherVideo) {
        this.player.queue = [];
        nextVideoId = this.relatedList.shift().id;
      } else {
        nextVideoId = this.player.queue[this.player.currentPlayIndex].id;
      }

      if (this.$route.params.id !== nextVideoId) {
        this.$router.push({ name: 'VideoPage', params: { id: nextVideoId } });
      }
    },

    '$store.state.user.loggedIn': function () {
      const { loggedIn } = this.$store.state.user;

      if (loggedIn) {
        this.fetchFavoriteStatus();
      }
    },
  },

  created() {
    this.videoId = this.$route.params.id;
    this.currentUrl = window.location.href;
    this.fetchData(this.$route.params.id);
  },

  mounted() {
    this.isFavorited = false;
    const isPlayNextVideo = localStorage.getItem('isPlayNextVideo');
    this.isPlayNextVideo = isPlayNextVideo === null
      ? false
      : Boolean(parseInt(isPlayNextVideo, 10));
    this.setIsPlayNext(this.isPlayNextVideo);
  },

  beforeRouteUpdate(to, from, next) {
    this.fetchData(to.params.id);
    this.isFavorited = false;
    next();
  },

  methods: {
    shareModal() {
      this.$FModal.show({
        component: ShareModal,
        overlayStyles: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
        },
      },
      {
        shareId: this.videoId,
        shareRoute: 'video',
        shareTitle: this.title,
      });
    },

    moreDescription() {
      this.$gtag.event('more_description', {
        event_category: 'video_related',
        event_label: 'User clicked read more',
        value: 1,
      });
    },

    changeIsPlayNext(current) {
      this.setIsPlayNext(current);
      this.$gtag.event('switch_next_video', {
        event_category: 'video_related',
        event_label: 'User clicked switch button next video',
        value: 1,
      });
    },

    setIsPlayNext(option) {
      localStorage.setItem('isPlayNextVideo', option ? '1' : '0');
    },

    setFallbackImageUrl(event) {
      const id = event.path[0].parentElement.getAttribute('vt-id');
      const image = event.path[0];

      if (image.naturalWidth === 120) {
        image.src = `https://i.ytimg.com/vi/${id}/mqdefault.jpg`;
      }
    },

    fetchData(id) {
      getVideoById(id, { headers: { locale: this.$i18n.locale } })
        .then(async (result) => {
          const videoData = result.data.data;

          this.id = videoData.id;
          this.title = videoData.title;
          this.youtubeId = videoData.provider_video_id;
          this.description = videoData.description;
          this.created_at = videoData.created_at;
          this.channel = videoData.channel;

          this.tags = videoData.tags.filter((item) => item.type === null);

          this.instrumentTags = videoData.tags.filter((item) => item.type !== null
              && !item.type.includes('genre')
              && !item.type.includes('composer'));

          this.genreTags = videoData.tags.filter((item) => item.type !== null
              && item.type.includes('genre'));

          if (!this.isQueueHasOtherVideo || this.youtubeId !== this.player.videoId) {
            const duplicatedIndex = this.player.queue.findIndex((video) => video.id === this.id);

            let currentIndex;

            if (duplicatedIndex === -1) {
              this.player.queue.push(videoData);
              currentIndex = this.player.queue.length - 1;
            } else {
              currentIndex = duplicatedIndex;
            }

            await store.dispatch('setPlayerCurrentPlay', {
              index: currentIndex,
            });

            await store.dispatch('setPlayerPlay');
          }

          this.loading = false;
          this.$emit('forGtagStatus', this.title);

          if (this.user.loggedIn) {
            this.fetchFavoriteStatus();
          }
        })
        .catch((err) => {
          console.error(err);

          if (err.request.status === 404) {
            this.$router.push({ name: '404' });
          }
        });

      getRelatedVideos(this.$route.params.id)
        .then((list) => {
          this.relatedList = list.data.data;
          this.videoId = this.$route.params.id;

          this.loading = false;
        })
        .catch((err) => { console.error(err); });
    },

    fetchFavoriteStatus() {
      getFavoriteStatus(this.$route.params.id)
        .then((status) => {
          this.isFavorited = status.data.is_added;
        });
    },

    // goToNextVideo(detail) {
    //   if (detail.code === 0 && this.isPlayNextVideo) {
    //     this.$router.push({ name: 'VideoPage', params: { id: this.relatedList.shift().id } });
    //   }
    // },

    addVideoFromFavorite(videoId) {
      if (this.$store.state.user.loggedIn) {
        addFavoriteVideo(videoId)
          .then(() => {
            this.isFavorited = true;

            this.$buefy.snackbar.open({
              duration: 2000,
              message: this.$t('performs.likedmsg'),
              type: 'is-dark',
              position: 'is-bottom',
              actionText: null,
            });
          })
          .catch((err) => {
            console.error(err);

            this.$buefy.snackbar.open({
              message: this.$t('msgs.servererror'),
              type: 'is-danger',
              position: 'is-bottom',
              actionText: this.$t('general.reload'),
              onAction: () => {
                window.location.reload();
              },
            });
          });
      } else {
        this.$router.push({ name: 'Signin' });
      }

      this.$gtag.event('like_video', {
        event_category: 'video_related',
        event_label: 'User clicked like button',
        value: 1,
      });
    },
  },
};
</script>
<style lang="scss">
.vue-foldable {
  position: relative;
}
.vue-foldable .vue-foldable-container {
  overflow: hidden;
}
.vue-foldable-container {
  transition: max-height 0.3s;
}
.vue-foldable-mask {
  transition: opacity 3s;
}
.vue-foldable .vue-foldable-mask {
  position: absolute;
  width: 100%;
  pointer-events: none;
  bottom: 15px;
  height: 100px;
}
.vue-foldable .vue-foldable-mask.collapsed {
  background: linear-gradient(to bottom, rgba(250, 250, 250, 0), #fafafa);
}
</style>
<style scoped>
.taghorizontal >>> .v-hl-btn svg {
  border-radius: 0;
  margin: 0;
  padding: 8px;
  height: 100%;
  box-shadow: none;
  background: none;
}

.taghorizontal >>> .v-hl-btn-prev {
  background: linear-gradient(to left, #fafafa00 0, #fafafa 66%, #fafafa);
}

.taghorizontal >>> .v-hl-btn-next {
  background: linear-gradient(to right, #fafafa00 0, #fafafa 66%, #fafafa);
}

.taghorizontal >>> .v-hl-btn {
  top: 0;
  bottom: 0;
  transform: translateX(0);
}
</style>
